import { canEditInvoice, canEditSalesTransaction } from './invoiceEditability';
import type { Invoice, InvoiceStatus } from '../types/invoice';
import type { SalesTransaction } from '../types/salesTransaction';
import type { CustomerPayment } from '../types/customerPayment';

/** ADMIN or ACCOUNTANT — create, edit, and post invoices. */
export const canManageInvoices = (): boolean => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

/** ADMIN, ACCOUNTANT, or CASHIER — copy invoice to new draft. */
export const canCopyInvoice = (): boolean => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT' || role === 'CASHIER';
};

/** ADMIN, ACCOUNTANT, or CASHIER — convert quotation to draft invoice. */
export const canConvertQuotationToInvoice = (): boolean => canCopyInvoice();

/** ADMIN only — void invoices and payments. */
export const canDeleteOrVoidSales = (): boolean =>
  localStorage.getItem('user_role')?.toUpperCase() === 'ADMIN';

/** ADMIN or ACCOUNTANT — edit customer payment allocations. */
export const canEditCustomerPayment = (): boolean => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

export const canEditInvoiceTransaction = (
  invoice: Pick<Invoice, 'status' | 'balance' | 'total_amount'>
): boolean => canManageInvoices() && canEditInvoice(invoice);

export const canEditInvoiceFromRow = (txn: SalesTransaction): boolean =>
  txn.transaction_type === 'INVOICE' && canManageInvoices() && canEditSalesTransaction(txn);

export const canPostInvoice = (invoice: Pick<Invoice, 'status'>): boolean =>
  canManageInvoices() && invoice.status === 'DRAFT';

export const canReceivePaymentForInvoice = (status: InvoiceStatus): boolean =>
  status === 'OPEN' || status === 'PARTIAL';

export const canDeleteDraftInvoice = (
  invoice: Pick<Invoice, 'status'>
): boolean => canManageInvoices() && invoice.status === 'DRAFT';

export const canVoidInvoice = (invoice: Pick<Invoice, 'status'>): boolean =>
  canDeleteOrVoidSales() && invoice.status === 'OPEN';

export const canEditPaymentTransaction = (
  payment: Pick<CustomerPayment, 'status'>
): boolean => canEditCustomerPayment() && !isPaymentVoided(payment);

export const canVoidPaymentTransaction = (payment: Pick<CustomerPayment, 'status'>): boolean => {
  const status = (payment.status ?? 'POSTED').toUpperCase();
  return canDeleteOrVoidSales() && status !== 'VOIDED';
};

export const isPaymentVoided = (payment: Pick<CustomerPayment, 'status'>): boolean =>
  (payment.status ?? 'POSTED').toUpperCase() === 'VOIDED';

/** ADMIN or ACCOUNTANT — create and edit quotations. */
export const canManageQuotations = (): boolean => {
  const role = localStorage.getItem('user_role')?.toUpperCase();
  return role === 'ADMIN' || role === 'ACCOUNTANT';
};

export const canEditQuotationFromRow = (txn: SalesTransaction): boolean =>
  txn.transaction_type === 'QUOTATION' && txn.available_actions.includes('edit');

export const canDeleteQuotationFromRow = (txn: SalesTransaction): boolean =>
  txn.transaction_type === 'QUOTATION' && txn.available_actions.includes('delete');
